import React, { memo, useEffect, useState, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { ShopContext } from '../context/shopcontext';
import axios from 'axios';
import AOS from 'aos';
import 'aos/dist/aos.css';

const CategoryCard = memo(({ category }) => {
  const navigate = useNavigate();

  useEffect(() => {
    AOS.init({ duration: 1200, easing: 'cubic-bezier(0.25, 0.46, 0.45, 0.94)' });
  }, []);

  return (
    <div
      className="group relative cursor-pointer transition-all duration-700 ease-out transform hover:scale-105 hover:-translate-y-1"
      onClick={() => navigate(`/collection?category=${category.slug}`)}
    >
      {/* Mirror Frame Container */}
      <div
        data-aos="fade-up"
        data-aos-delay="150"
        className="relative w-48 h-60 sm:w-40 sm:h-52 lg:w-60 lg:h-80"
      >
        {/* Main Mirror Oval */}
        <div
          className="relative w-full h-full transition-all duration-700 group-hover:rotate-1"
          style={{
            background: `
              radial-gradient(ellipse at 30% 20%, 
                rgba(235, 226, 219, 1) 0%, 
                rgba(224, 195, 181, 0.95) 15%,
                rgba(176, 146, 135, 0.85) 35%,
                rgba(235, 226, 219, 0.98) 60%,
                rgba(224, 195, 181, 0.92) 85%,
                rgba(176, 146, 135, 0.9) 100%
              )
            `,
            borderRadius: '50%',
            border: '3px solid rgba(176, 146, 135, 0.6)',
            boxShadow: `
              0 12px 40px -12px rgba(45, 22, 30, 0.25),
              inset 0 2px 4px rgba(255, 255, 255, 0.9),
              inset 0 -2px 6px rgba(176, 146, 135, 0.3),
              inset 2px 0 4px rgba(255, 255, 255, 0.4),
              inset -2px 0 4px rgba(176, 146, 135, 0.2),
              0 0 0 1px rgba(176, 146, 135, 0.2),
              0 4px 12px rgba(176, 146, 135, 0.15)
            `,
          }}
        >
          {/* Mirror Surface */}
          <div
            className="absolute inset-2 rounded-full overflow-hidden"
            style={{
              background: `
                radial-gradient(ellipse at 35% 25%, 
                  rgba(255, 255, 255, 0.9) 0%, 
                  rgba(255, 255, 255, 0.3) 25%,
                  rgba(235, 226, 219, 0.2) 45%,
                  transparent 70%
                ),
                radial-gradient(ellipse at 70% 80%, 
                  rgba(176, 146, 135, 0.15) 0%, 
                  transparent 50%
                ),
                linear-gradient(135deg, 
                  rgba(235, 226, 219, 0.6) 0%, 
                  rgba(224, 195, 181, 0.4) 30%,
                  rgba(176, 146, 135, 0.25) 50%,
                  rgba(235, 226, 219, 0.5) 80%,
                  rgba(224, 195, 181, 0.3) 100%
                )
              `,
              backdropFilter: 'blur(0.5px)',
              boxShadow: `
                inset 0 1px 3px rgba(255, 255, 255, 0.8),
                inset 0 -1px 2px rgba(176, 146, 135, 0.4)
              `,
            }}
          >
            {/* Jewelry Image */}
            <div className="relative w-full h-full flex items-center justify-center overflow-hidden rounded-full">
              <img
                src={`${category.image}?w=250&h=250&c_fill&f=auto&q=95`}
                alt={category.name}
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-110"
                style={{
                  filter: 'brightness(1.1) contrast(1.15) saturate(1.05)',
                  borderRadius: '50%',
                }}
              />

              {/* Mirror Reflection Effect */}
              <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-700">
                <div
                  className="absolute top-0 left-0 w-full h-full rounded-full"
                  style={{
                    background: `
                      linear-gradient(45deg, transparent 0%, rgba(255, 255, 255, 0.4) 25%, transparent 50%)
                    `,
                    animation: 'mirrorShimmer 2.5s infinite ease-in-out',
                  }}
                />
              </div>

              {/* Additional Metal Reflection */}
              <div className="absolute inset-0 rounded-full pointer-events-none">
                <div
                  className="absolute top-1/4 left-1/4 w-8 h-8 rounded-full opacity-20"
                  style={{
                    background:
                      'radial-gradient(circle, rgba(255, 255, 255, 0.8) 0%, transparent 70%)',
                    filter: 'blur(4px)',
                    animation: 'metalGlint 3s infinite ease-in-out',
                  }}
                />
              </div>
            </div>
          </div>

          {/* Enhanced Mirror Frame Highlights */}
          <div className="absolute inset-0 rounded-full pointer-events-none">
            {/* Top highlight - enhanced */}
            <div
              className="absolute top-0 left-1/4 right-1/4 h-2 rounded-full opacity-80"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.9), rgba(255, 255, 255, 0.6), rgba(255, 255, 255, 0.9), transparent)',
                filter: 'blur(1px)',
              }}
            />

            {/* Side highlights */}
            <div
              className="absolute left-0 top-1/4 bottom-1/4 w-1 rounded-full opacity-40"
              style={{
                background:
                  'linear-gradient(180deg, transparent, rgba(255, 255, 255, 0.7), transparent)',
              }}
            />
            <div
              className="absolute right-0 top-1/4 bottom-1/4 w-1 rounded-full opacity-20"
              style={{
                background:
                  'linear-gradient(180deg, transparent, rgba(176, 146, 135, 0.8), transparent)',
              }}
            />

            {/* Bottom shadow - enhanced */}
            <div
              className="absolute bottom-0 left-1/4 right-1/4 h-2 rounded-full opacity-50"
              style={{
                background:
                  'linear-gradient(90deg, transparent, rgba(176, 146, 135, 0.8), rgba(45, 22, 30, 0.3), rgba(176, 146, 135, 0.8), transparent)',
                filter: 'blur(2px)',
              }}
            />

            {/* Hover rim glow - enhanced */}
            <div
              className="absolute inset-0 rounded-full opacity-0 group-hover:opacity-100 transition-opacity duration-500"
              style={{
                background: `
                  radial-gradient(ellipse at 30% 30%, rgba(255, 255, 255, 0.3) 0%, transparent 40%),
                  linear-gradient(135deg, rgba(176, 146, 135, 0.3) 0%, transparent 30%, rgba(176, 146, 135, 0.2) 70%, transparent 100%)
                `,
                boxShadow: 'inset 0 0 8px rgba(176, 146, 135, 0.2)',
              }}
            />

            {/* Metal beveled edge effect */}
            <div
              className="absolute inset-1 rounded-full opacity-60"
              style={{
                background: `
                  conic-gradient(
                    rgba(255, 255, 255, 0.6) 0deg,
                    rgba(176, 146, 135, 0.3) 90deg,
                    rgba(45, 22, 30, 0.2) 180deg,
                    rgba(176, 146, 135, 0.3) 270deg,
                    rgba(255, 255, 255, 0.6) 360deg
                  )
                `,
                mask: 'radial-gradient(circle, transparent 85%, black 87%, black 92%, transparent 94%)',
                WebkitMask:
                  'radial-gradient(circle, transparent 85%, black 87%, black 92%, transparent 94%)',
              }}
            />
          </div>

          {/* Enhanced Outer Glow */}
          <div
            className="absolute -inset-2 rounded-full opacity-0 group-hover:opacity-100 transition-all duration-700"
            style={{
              background: `
                radial-gradient(ellipse at center, 
                  rgba(176, 146, 135, 0.2) 0%, 
                  rgba(176, 146, 135, 0.1) 40%,
                  transparent 70%
                )
              `,
              filter: 'blur(12px)',
            }}
          />

          {/* Subtle surface texture */}
          <div
            className="absolute inset-1 rounded-full opacity-30 pointer-events-none"
            style={{
              background: `
                repeating-conic-gradient(
                  from 0deg at 50% 50%,
                  transparent 0deg,
                  rgba(176, 146, 135, 0.1) 2deg,
                  transparent 4deg
                )
              `,
              filter: 'blur(0.5px)',
            }}
          />
        </div>

        {/* Enhanced Base Shadow */}
        <div
          className="absolute -bottom-1 left-1/2 transform -translate-x-1/2 w-3/4 h-1 opacity-25"
          style={{
            background:
              'radial-gradient(ellipse, rgba(45, 22, 30, 0.6) 0%, rgba(176, 146, 135, 0.2) 50%, transparent 100%)',
            borderRadius: '50%',
            filter: 'blur(2px)',
          }}
        />
      </div>

      {/* Category Name Below Card */}
      <div className="mt-4 text-center">
        <h3
          className="text-sm sm:text-base lg:text-lg font-semibold tracking-wide uppercase transition-colors duration-300 group-hover:scale-105 transform"
          style={{
            fontFamily: 'Cinzel, serif',
            color: '#2d161e',
            textShadow: '0 1px 2px rgba(176, 146, 135, 0.2)',
          }}
        >
          {category.name}
        </h3>

        <div
          className="mt-2 mx-auto transition-all duration-500 group-hover:scale-x-125"
          style={{
            width: '32px',
            height: '1px',
            background: 'linear-gradient(90deg, transparent, #b09287, transparent)',
          }}
        />
      </div>
    </div>
  );
});

const CategorySection = () => {
  const [categories, setCategories] = useState([]);
  const { backendUrl } = useContext(ShopContext);

  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const res = await axios.get(`${backendUrl}/api/category/list`);
        if (res.data.success) {
          setCategories(res.data.categories);
        }
      } catch (error) {
        console.error('Error fetching jewelry categories:', error.message);
      }
    };

    fetchCategories();
  }, [backendUrl]);

  return (
    <section className="relative py-20 lg:py-28 overflow-hidden">
      {/* Ambient Background Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div
          className="absolute top-1/4 left-1/4 w-0.5 h-0.5 rounded-full opacity-20 animate-pulse"
          style={{ background: 'radial-gradient(circle, #b09287, transparent)' }}
        />
        <div
          className="absolute top-3/4 right-1/4 w-1 h-1 rounded-full opacity-15 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #e0c3b5, transparent)',
            animationDelay: '1s',
          }}
        />
        <div
          className="absolute top-1/2 left-3/4 w-0.5 h-0.5 rounded-full opacity-25 animate-pulse"
          style={{
            background: 'radial-gradient(circle, #b09287, transparent)',
            animationDelay: '2s',
          }}
        />
      </div>

      <div className="relative px-6 sm:px-8 lg:px-12 max-w-7xl mx-auto">
        {/* Refined Header */}
        <div className="text-center mb-16" data-aos="fade-down" data-aos-duration="1000">
          <div className="flex items-center justify-center mb-6">
            <div className="w-12 h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent" />
            <div className="mx-6 relative">
              <h2
                className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-wider relative"
                style={{
                  fontFamily: 'Cinzel, serif',
                  background: 'linear-gradient(135deg, #2d161e 0%, #4c0f2e 50%, #2d161e 100%)',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                }}
              >
                CHOOSE BY CATEGORY
                <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-20 h-0.5 bg-gradient-to-r from-transparent via-gray-400 to-transparent rounded-full" />
              </h2>
            </div>
            <div className="w-12 h-px bg-gradient-to-r from-transparent via-gray-400 to-transparent" />
          </div>

          <p
            className="text-base sm:text-lg font-light italic max-w-xl mx-auto leading-relaxed"
            style={{
              fontFamily: 'Lato, sans-serif',
              background: 'linear-gradient(135deg, #4c0f2e 0%, #b09287 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text',
            }}
          >
            Timeless elegance crafted with precision and adorned with beauty
          </p>
        </div>

        {/* Mirror Grid */}
        <div className="flex flex-wrap justify-center gap-4 sm:gap-6 lg:gap-8">
          {categories.map((category, index) => (
            <div key={category.slug} data-aos="zoom-in" data-aos-delay={index * 100}>
              <CategoryCard category={category} />
            </div>
          ))}
        </div>

        {/* Bottom Accent */}
        <div className="mt-16 flex justify-center" data-aos="fade-up" data-aos-delay="600">
          <div className="w-24 h-px bg-gradient-to-r from-transparent via-gray-300 to-transparent" />
        </div>
      </div>

      {/* Enhanced Styles */}
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Cinzel:wght@400;500;600;700&family=Lato:ital,wght@0,300;0,400;0,700;1,300;1,400&display=swap');
        
        @keyframes mirrorShimmer {
          0% { transform: translateX(-100%) rotate(45deg); opacity: 0; }
          30% { opacity: 0.4; }
          70% { opacity: 0.4; }
          100% { transform: translateX(200%) rotate(45deg); opacity: 0; }
        }

        @keyframes metalGlint {
          0% { opacity: 0.2; transform: scale(1) rotate(0deg); }
          50% { opacity: 0.6; transform: scale(1.2) rotate(180deg); }
          100% { opacity: 0.2; transform: scale(1) rotate(360deg); }
        }
      `}</style>
    </section>
  );
};

export default memo(CategorySection);
